import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Building2, Check, Loader2, ShieldCheck, Unplug, ExternalLink, AlertTriangle, X, Copy, Zap } from "lucide-react";
import { toast } from "sonner";

export default function AgencyPairingPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hubUrl, setHubUrl] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastAck, setLastAck] = useState(null);
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const checkHub = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const { data } = await api.post("/agency/check-hub");
      setCheckResult(data);
      if (data.ok) toast.success(`Accès Agence OK — v${data.version} (${data.phase})`);
      else if (data.reachable) toast.warning("Accès Agence joignable mais non compatible — mettez-le à jour");
      else toast.error("Accès Agence injoignable");
    } catch (e) {
      toast.error("Erreur diagnostic");
    } finally { setChecking(false); }
  };

  const sendHeartbeat = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/agency/send-heartbeat");
      if (data.ok) toast.success("Snapshot envoyé à l'Accès Agence ✓");
      else toast.warning(`Échec : ${data.error || "réseau"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/agency/pairing-status"); setStatus(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const claim = async () => {
    if (!hubUrl.trim() || !pairingCode.trim()) {
      toast.error("URL de l'Accès Agence et code d'appairage requis"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/agency/claim", { hub_url: hubUrl.trim(), pairing_code: pairingCode.trim().toUpperCase() });
      setLastAck(data);
      if (data.ack_ok) toast.success("Site appairé avec succès à l'Accès Agence ✓");
      else toast.warning(`Appairage local enregistré, mais l'Accès Agence n'a pas répondu : ${data.ack_error || "réseau"}`);
      await load();
      setHubUrl(""); setPairingCode("");
    } catch (e) { toast.error("Erreur : " + (e?.response?.data?.detail || e.message)); }
    finally { setSubmitting(false); }
  };

  const unpair = async () => {
    if (!window.confirm("Désappairer ce site de l'Accès Agence ? L'agence ne pourra plus gérer ce site à distance.")) return;
    await api.delete("/agency/pairing");
    toast.success("Site désappairé"); await load();
  };

  return (
    <div className="p-6 md:p-10 max-w-3xl space-y-5" data-testid="agency-pairing">
      <header>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
          <Building2 className="w-3.5 h-3.5" /> Connexion à votre agence
        </div>
        <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Appairer ce site à l'Accès Agence</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Liez votre site à l'Accès Agence pour que l'équipe puisse le piloter, ouvrir le back-office en un clic et suivre les tickets support.
        </p>
      </header>

      {loading ? <Card className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></Card> : status?.paired ? (
        // ========= PAIRED =========
        <Card className="p-6 bg-gradient-to-br from-emerald-50 to-transparent border-emerald-200">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg text-[color:var(--brand-navy)]">Site appairé ✓</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">Votre site est connecté à l'Accès Agence.</p>
            </div>
            <Badge className="bg-emerald-500 text-white border-0">Actif</Badge>
          </div>
          <div className="space-y-2 bg-white/60 rounded-lg p-4 border border-emerald-100">
            <InfoLine label="URL du HUB" value={status.hub_url}>
              <a href={status.hub_url} target="_blank" rel="noopener noreferrer" className="text-[color:var(--brand-sky)] hover:underline text-xs">
                <ExternalLink className="w-3 h-3 inline" /> Ouvrir
              </a>
            </InfoLine>
            <InfoLine label="Code d'appairage" value={<span className="font-mono font-bold tracking-widest">{status.pairing_code}</span>} />
            {status.paired_at && <InfoLine label="Appairé le" value={new Date(status.paired_at).toLocaleString("fr-FR")} />}
          </div>
          <div className="mt-4 flex flex-wrap justify-between items-center gap-2">
            <div className="flex gap-2">
              <Button variant="outline" onClick={checkHub} disabled={checking} data-testid="check-hub-btn">
                {checking ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <ShieldCheck className="w-4 h-4 mr-1.5"/>}
                Diagnostiquer l'Accès Agence
              </Button>
              <Button variant="outline" onClick={sendHeartbeat} disabled={sending} data-testid="send-heartbeat-btn">
                {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Zap className="w-4 h-4 mr-1.5"/>}
                Pousser un snapshot à l'Accès Agence
              </Button>
            </div>
            <Button variant="outline" onClick={unpair} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unpair-btn">
              <Unplug className="w-4 h-4 mr-1.5" /> Désappairer
            </Button>
          </div>

          {checkResult && (
            <div className={`mt-3 p-3 rounded-lg border text-sm ${checkResult.compatible ? "bg-emerald-50 border-emerald-300 text-emerald-900" : checkResult.reachable ? "bg-amber-50 border-amber-300 text-amber-900" : "bg-red-50 border-red-300 text-red-900"}`} data-testid="check-result">
              {checkResult.compatible ? (
                <>
                  <div className="font-bold flex items-center gap-1"><Check className="w-4 h-4"/>Accès Agence compatible</div>
                  <div className="text-xs mt-1">Version {checkResult.version} · Phase {checkResult.phase} · Mode Accès Agence activé</div>
                  <div className="text-xs mt-1 opacity-80">Features : {checkResult.features?.slice(0, 4).join(", ")}{checkResult.features?.length > 4 ? "…" : ""}</div>
                </>
              ) : checkResult.reachable ? (
                <>
                  <div className="font-bold flex items-center gap-1"><AlertTriangle className="w-4 h-4"/>Accès Agence à mettre à jour</div>
                  <div className="text-xs mt-1">{checkResult.error}</div>
                  {checkResult.version && <div className="text-xs mt-1 opacity-80">Version actuelle : {checkResult.version}</div>}
                </>
              ) : (
                <>
                  <div className="font-bold flex items-center gap-1"><X className="w-4 h-4"/>Accès Agence injoignable</div>
                  <div className="text-xs mt-1">{checkResult.error}</div>
                </>
              )}
            </div>
          )}
        </Card>
      ) : (
        // ========= NOT PAIRED =========
        <>
          <Card className="p-6">
            <h2 className="font-display font-bold text-lg text-[color:var(--brand-navy)] mb-1">Entrer le code d'appairage</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
              Votre agence vous a transmis un code à 6 caractères. Saisissez-le ci-dessous avec l'URL de son Accès Agence.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">URL de l'Accès Agence</Label>
                <Input
                  value={hubUrl}
                  onChange={e => setHubUrl(e.target.value)}
                  placeholder="https://hub.agence-msc.fr"
                  data-testid="hub-url-input"
                />
              </div>
              <div>
                <Label className="text-xs">Code d'appairage (6 caractères)</Label>
                <Input
                  value={pairingCode}
                  onChange={e => setPairingCode(e.target.value.toUpperCase())}
                  placeholder="AB34CD"
                  className="font-mono tracking-widest text-lg uppercase"
                  maxLength={6}
                  data-testid="pairing-code-input"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={claim} disabled={submitting} className="bg-[color:var(--brand-navy)]" data-testid="claim-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                  Appairer ce site
                </Button>
              </div>
            </div>
          </Card>

          {lastAck && !lastAck.ack_ok && (
            <Card className="p-4 bg-amber-50 border-amber-200 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="text-xs text-amber-900">
                <div className="font-bold mb-1">Appairage partiel</div>
                <p>Le code a été enregistré localement, mais l'Accès Agence n'a pas confirmé la réception : <code className="bg-amber-100 px-1 rounded">{lastAck.ack_error}</code>. Vérifiez l'URL de l'Accès Agence, ou contactez votre agence.</p>
              </div>
            </Card>
          )}

          <Card className="p-4 bg-[color:var(--brand-sky)]/5 border-[color:var(--brand-sky)]/20">
            <div className="flex items-start gap-3 text-xs text-[color:var(--brand-navy)]">
              <div className="w-8 h-8 rounded-lg bg-[color:var(--brand-sky)]/15 text-[color:var(--brand-sky)] flex items-center justify-center shrink-0">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <div>
                <div className="font-bold mb-1">Que fait l'appairage ?</div>
                <ul className="space-y-0.5 text-[hsl(var(--muted-foreground))]">
                  <li>✓ Votre agence peut piloter votre site à distance (ping, version, statistiques)</li>
                  <li>✓ Les tickets support que vous ouvrez remontent dans son Accès Agence</li>
                  <li>✓ Elle peut ouvrir votre back-office en un clic (avec vos identifiants)</li>
                  <li>✓ Vous recevez les nouvelles versions du COCKPIT plus rapidement</li>
                </ul>
                <div className="font-bold mt-2 mb-0.5">Ce que l'appairage ne fait PAS :</div>
                <ul className="space-y-0.5 text-[hsl(var(--muted-foreground))]">
                  <li>✗ L'agence ne se connecte PAS automatiquement (elle doit toujours saisir votre mot de passe admin)</li>
                  <li>✗ Aucune donnée client/commande n'est envoyée à l'Accès Agence sans votre accord</li>
                </ul>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function InfoLine({ label, value, children }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm py-1">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">{label}</div>
      <div className="flex items-center gap-2">
        <div className="truncate max-w-[320px]">{value}</div>
        {children}
      </div>
    </div>
  );
}
